<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import AppHeader from '@/components/layout/AppHeader.vue'
import AppRail from '@/components/layout/AppRail.vue'
import MobileNavSheet from '@/components/layout/MobileNavSheet.vue'
import MobileTabBar from '@/components/layout/MobileTabBar.vue'
import SectionNav from '@/components/layout/SectionNav.vue'
import SectionTabs from '@/components/layout/SectionTabs.vue'
import { NAVIGATION } from '@/constants/app/navigation.constant.js'
import { useAuthStore } from '@/stores/auth/auth.store.js'
import { useNavCollapse } from '@/composables/navigation/use-nav-collapse.js'
import { activeSectionId, firstRouteIn, visibleSections } from '@/utils/visible-navigation.util.js'

/**
 * The authenticated shell: rail, section nav, header, content.
 *
 * The permission filtering is in `visible-navigation.ts` so it can be tested without
 * mounting any of this. What stays here is only the wiring — which section is selected,
 * and what that means for the nav beside it.
 */
const auth = useAuthStore()
const route = useRoute()
const router = useRouter()

const sections = computed(() => visibleSections(NAVIGATION, auth.canAll))

/**
 * The active rail section: a click wins until the next navigation; otherwise the section
 * that owns the current route (a detail route names its list route as `meta.parent`).
 * The rule itself lives in `visible-navigation.util.ts`, where it is tested.
 */
const chosen = ref<string | null>(null)

const routeName = computed(() => {
  const name = typeof route.meta.parent === 'string' ? route.meta.parent : route.name

  return typeof name === 'string' ? name : null
})

/** Collapsing the section nav to the icon rail. Remembered per browser. */
const { collapsed, toggle } = useNavCollapse()

/** The "More" sheet on a phone. Closed by any navigation, like the rail click it replaces. */
const menuOpen = ref(false)

// A navigation clears the click: the operator is now somewhere, and the rail shows where.
watch(() => route.fullPath, () => {
  chosen.value = null
  menuOpen.value = false
})

/** The sections the tab bar could not fit, for the sheet. Mirrors `MobileTabBar`'s split. */
const overflowSections = computed(() => sections.value.slice(4))

const activeId = computed(() => activeSectionId(sections.value, routeName.value, chosen.value) ?? '')
const active = computed(() => sections.value.find((section) => section.id === activeId.value))
const owning = computed(() => activeSectionId(sections.value, routeName.value, null))

/**
 * A rail click shows the section's nav AND goes to its first screen. Showing the nav alone
 * read as "nothing happened" — the content did not change — which is the complaint that
 * led here. If the section has no built screen yet, only the nav changes.
 */
async function selectSection(id: string): Promise<void> {
  chosen.value = id

  const section = sections.value.find((candidate) => candidate.id === id)
  const target = section === undefined ? null : firstRouteIn(section)

  if (target !== null) await router.push({ name: target.name, query: target.query ?? {} })
}

const crumbSection = computed(() => {
  // Only the section that OWNS the route goes in the crumb — never a mere rail click.
  const owner = sections.value.find((section) => section.id === owning.value)

  return owner?.items.some((item) => item.route !== null && item.route === routeName.value) === true ? owner.label : null
})
</script>

<template>
  <!-- `h-dvh`, not `h-screen`: a phone's address bar shrinks the viewport as it scrolls, and 100vh is the LARGER of the two, which hides the tab bar behind the browser chrome. -->
  <div class="flex h-dvh overflow-hidden bg-canvas">
    <AppRail
      :sections="sections"
      :active-id="activeId"
      :collapsed="collapsed"
      @select="selectSection($event)"
    />

    <SectionNav
      v-if="active !== undefined && !collapsed"
      :label="active.label"
      :items="active.items"
      @collapse="toggle()"
    />

    <div class="flex min-w-0 flex-1 flex-col">
      <AppHeader
        :section-label="crumbSection"
        :nav-collapsed="collapsed"
        @toggle-nav="toggle()"
      />
      <SectionTabs
        v-if="active !== undefined"
        :label="active.label"
        :items="active.items"
      />

      <!--
        The one scroll container: the rail and nav stay put while content moves.

        `relative` is load-bearing. `sr-only` captions and labels are `position: absolute`,
        and an absolute box is clipped only by a positioned ancestor — with none, their
        containing block is the document, so a hidden caption sitting 3,000px down a long
        table stretched the PAGE to that height and the window grew a second scrollbar.
      -->
      <main class="relative min-h-0 flex-1 overflow-y-auto px-4 py-5 lg:px-8 lg:py-7">
        <slot />
      </main>

      <MobileTabBar
        :sections="sections"
        :active-id="activeId"
        :menu-open="menuOpen"
        @select="selectSection($event)"
        @more="menuOpen = !menuOpen"
      />
    </div>

    <MobileNavSheet
      :open="menuOpen"
      :sections="overflowSections"
      @close="menuOpen = false"
    />
  </div>
</template>
